import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useIsRoomMember } from "../../domains/room/hooks/useIsRoomMember";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useApiQuery } from "../../services/api";
import { getRoomPathname } from "../../domains/room/utils/getRoomPathname";
import { useCanShowJoin } from "../../domains/room/hooks/useCanShowJoin";
import { useJoinRoom } from "../../domains/room/hooks/useJoinRoom";
import Loader from "../Loader";
import type { DiscoverableRoomOffer } from "../../features/article/hooks/useArticleData";

type Props = {
  offer: DiscoverableRoomOffer | null;
  isRoomJoinable: boolean;
};

const BUTTON_CLASS =
  "shrink-0 rounded-2xl border border-[rgba(14,59,50,0.2)] bg-white px-6 py-3 text-sm font-semibold text-lime-background-3 h-9 flex items-center justify-center";

export default function DiscoverableRoomBlock(props: Props) {
  const { offer, isRoomJoinable } = props;
  const history = useHistory();
  const { t } = useTranslation();
  const { isAuthenticated } = useCurrentUser();
  // INTERFACE locale: this promotes a room the reader is NOT in, from an article
  // page. `contentLang` is the current room content language and is meaningless here.
  const { activeLang } = useActiveLang();
  const isMember = useIsRoomMember(offer === null ? null : offer.room.id);

  if (offer === null) return null;
  const { room } = offer;
  if (isMember) return null;

  // Opens the room for every reader. A signed-out one meets the guard of
  // their platform on the room URL: the install wall on mobile web, which
  // hands the room to the app, the memorized path and `/welcome` in the native
  // shell, and the sign-in gate on desktop.
  function handleDiscover() {
    history.push(getRoomPathname(room.id));
  }

  return (
    <RoomOfferBanner
      roomName={getRoomName(room, activeLang)}
      action={
        !isRoomJoinable ? null : offer.action === "join" ? (
          <JoinButton room={room} />
        ) : (
          <button
            type="button"
            className={BUTTON_CLASS}
            onClick={handleDiscover}
          >
            {isAuthenticated ? t("Discover") : t("Register")}
          </button>
        )
      }
    />
  );
}

/**
 * The same offer on a room RESOURCE (a video, an infographic, a podcast) that a
 * reader opened from a room link.
 *
 * Every piece of room content is shareable, and a discoverable room invites the
 * reader who followed the link to join it (decided 2026-09-24,
 * `docs/shared-content-access/README.md`). An article learns its room from
 * `/articles/:id/discoverable-rooms`; a resource page knows its room from the
 * URL, so it asks for the room row and for `join/check` directly.
 *
 * It shows nothing to a reader with no session (the install ask or the sign-in
 * gate already stands over the page), to a member, or when `join/check` says no:
 * a private client space refuses its row to a stranger and takes its members by
 * invitation.
 */
export function RoomJoinOffer({ roomId }: { roomId: string }) {
  const { isAuthenticated } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const isMember = useIsRoomMember(roomId);
  const joinCheckQuery = useApiQuery(
    "/webapp/private/rooms/:id/join/check",
    isAuthenticated ? { param: { id: roomId }, durable: true } : false
  );
  const isJoinable =
    joinCheckQuery.state === "success" && joinCheckQuery.body === true;
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    isJoinable ? { param: { id: roomId } } : false
  );

  if (isMember || roomQuery.state !== "success") return null;
  const room = roomQuery.body;

  return (
    <RoomOfferBanner
      roomName={getRoomName(room, activeLang)}
      action={<JoinButton room={room} />}
    />
  );
}

function RoomOfferBanner({
  roomName,
  action,
}: {
  roomName: string;
  action: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-[19px] my-3 flex items-center gap-[10px] rounded-2xl bg-lime-background-3 py-2 pl-3 pr-2">
      <div className="flex flex-1 flex-col text-white leading-tight">
        <span className="text-xs">{t("Published in")}</span>
        <span className="text-sm font-semibold">{roomName}</span>
      </div>
      {action}
    </div>
  );
}

/**
 * Joins where the reader stands: they followed a colleague's link into this
 * room, and the content stays on screen once the room is theirs (the offer then
 * hides itself, since the room is in their list). The welcome sheet opens
 * over it.
 */
function JoinButton(props: {
  room: { id: string } & Parameters<typeof useCanShowJoin>[1];
}) {
  const { room } = props;
  const { t } = useTranslation();
  // The screenshot-session clause lives in here; the server's `join/check`
  // (`isRoomJoinable`) does not know about captures.
  const canShowJoin = useCanShowJoin(room.id, room);
  const { joinRoom, isJoining } = useJoinRoom(
    room.id,
    "shared article",
    "greet"
  );

  if (!canShowJoin) return null;

  return (
    <button
      type="button"
      className={BUTTON_CLASS}
      disabled={isJoining}
      onClick={() => {
        void joinRoom();
      }}
    >
      {isJoining ? <Loader /> : t("Join")}
    </button>
  );
}
